import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { getOrganizationId } from '@core-mkt/services/schema/organization.schema';

export interface TrustpilotSchemaData {
  /** Absolute URL of the site root (e.g. https://www.aceable.com/). Trailing slash optional. */
  siteUrl: string;
  /** Brand / organization display name (e.g. "Aceable"). */
  organizationName: string;
  /** Trustpilot trust score (e.g. 4.8). */
  trustScore: number;
  /** Total number of reviews from Trustpilot. */
  reviewCount: number;
}

/**
 * Returns a rich `EducationalOrganization` node with `aggregateRating` from
 * Trustpilot. Uses the same `#organization` `@id` as the top-section variant —
 * `ServerSchemaService` first-write-wins dedup ensures only one Org node ends
 * up in the `@graph`, and the universal fallback in `template-single` is dropped
 * whenever Trustpilot data is available on the page.
 *
 * Returns `null` when rating data is missing or zero so the rich Org never
 * shadows a richer registration with empty values.
 */
export function getTrustpilotSchema(data: TrustpilotSchemaData | null): SchemaOrgNode | null {
  if (!data || !data.organizationName || !data.siteUrl?.trim()) return null;

  // SECURITY: coerce upstream API values to numbers and reject anything
  // non-finite. The Trustpilot API is trusted today, but a manipulated /
  // mocked response that returned strings (e.g. `"4.8</script>..."`) would
  // otherwise embed attacker-controlled text into JSON-LD. The escape in
  // `server-schema.service.ts` is the last line of defense; this guard is
  // type-level enforcement at the data boundary.
  const trustScore = Number(data.trustScore);
  const reviewCount = Number(data.reviewCount);
  if (!Number.isFinite(trustScore) || trustScore <= 0) return null;
  if (!Number.isFinite(reviewCount) || reviewCount <= 0) return null;

  const siteUrl = data.siteUrl.endsWith('/') ? data.siteUrl : `${data.siteUrl}/`;

  return {
    '@type': 'EducationalOrganization',
    '@id': getOrganizationId(siteUrl),
    name: data.organizationName,
    url: siteUrl,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: trustScore,
      bestRating: 5,
      reviewCount,
    },
  };
}
